import React, { useEffect, useRef, useState } from "react";
import { AlertTriangle, Clock3, MapPin, ReceiptText, X } from "lucide-react";
import { useLocation } from "react-router-dom";

const SESSION_KEY = "shippingDelayNotice_2026_10";
const COOKIE_CONSENT_KEY = "cookieConsent_v2";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_SETTINGS = {
  enabled: false,
  title: "Possibili ritardi nelle consegne",
  updated_label: "Aggiornamento del 26 ottobre, ore 9:28",
  operativity: "Alcune filiali stanno riprendendo gradualmente l’attività. Le spedizioni tramite intermediari potrebbero subire blocchi prolungati.",
  delays: "Le agitazioni hanno causato accumuli di merce e ritardi significativi, con possibili rallentamenti sulla rete nazionale nelle prossime settimane.",
  caps: "I CAP nelle zone bloccate o limitrofe possono essere temporaneamente non disponibili per evitare spedizioni non gestibili.",
  refunds: "Se un incarico viene rifiutato da BRT o Poste Italiane, etichetta e importo vengono annullati e rimborsati automaticamente. I tempi di accredito dipendono dalla banca.",
  footer: "I vettori non hanno indicato tempistiche certe di ripristino. Monitoriamo costantemente la situazione.",
  updated_at: "initial",
};

const noticeItems = (settings) => [
  {
    icon: AlertTriangle,
    title: "Operatività selettiva",
    text: settings.operativity,
  },
  {
    icon: Clock3,
    title: "Ritardi e giacenze",
    text: settings.delays,
  },
  {
    icon: MapPin,
    title: "CAP temporaneamente limitati",
    text: settings.caps,
  },
  {
    icon: ReceiptText,
    title: "Rimborsi automatici",
    text: settings.refunds,
  },
];

export const ShippingDelayNotice = () => {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [settingsReady, setSettingsReady] = useState(false);
  const closeButtonRef = useRef(null);
  const isPublicStore = !pathname.startsWith("/gestione") && !pathname.startsWith("/paga");

  useEffect(() => {
    if (!isPublicStore) return undefined;
    let cancelled = false;
    fetch(`${API}/site-notices/shipping-delay`)
      .then((response) => response.ok ? response.json() : Promise.reject(new Error("notice unavailable")))
      .then((data) => {
        if (!cancelled) setSettings({ ...DEFAULT_SETTINGS, ...data });
      })
      .catch(() => {
        if (!cancelled) setSettings(DEFAULT_SETTINGS);
      })
      .finally(() => {
        if (!cancelled) setSettingsReady(true);
      });
    return () => { cancelled = true; };
  }, [isPublicStore]);

  useEffect(() => {
    if (!isPublicStore || !settingsReady || !settings.enabled) return undefined;
    const sessionKey = `${SESSION_KEY}_${settings.updated_at || "initial"}`;
    const reveal = () => {
      try {
        if (sessionStorage.getItem(sessionKey)) return;
      } catch (_) {
        return;
      }
      window.setTimeout(() => setOpen(true), 250);
    };

    try {
      if (localStorage.getItem(COOKIE_CONSENT_KEY)) reveal();
      else window.addEventListener("cookieConsentChanged", reveal, { once: true });
    } catch (_) {
      reveal();
    }
    return () => window.removeEventListener("cookieConsentChanged", reveal);
  }, [isPublicStore, settings.enabled, settings.updated_at, settingsReady]);

  useEffect(() => {
    if (!open) return undefined;
    closeButtonRef.current?.focus();
    const closeOnEscape = (event) => {
      if (event.key === "Escape") dismiss();
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [open]);

  const dismiss = () => {
    setOpen(false);
    try {
      sessionStorage.setItem(`${SESSION_KEY}_${settings.updated_at || "initial"}`, "dismissed");
    } catch (_) {
      // The notice remains usable when browser storage is unavailable.
    }
  };

  if (!open || !isPublicStore || !settings.enabled) return null;

  return (
    <div className="shipping-delay-notice-overlay" role="presentation">
      <section
        className="shipping-delay-notice-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="shipping-delay-notice-title"
        aria-describedby="shipping-delay-notice-description"
        data-testid="shipping-delay-notice"
      >
        <button
          ref={closeButtonRef}
          type="button"
          onClick={dismiss}
          className="shipping-delay-notice-close"
          aria-label="Chiudi avviso ritardi spedizioni"
          data-testid="shipping-delay-notice-close-button"
        >
          <X size={20} aria-hidden="true" />
        </button>
        <div className="shipping-delay-notice-heading">
          <AlertTriangle size={22} aria-hidden="true" />
          <div>
            <p className="shipping-delay-notice-kicker">Avviso spedizioni</p>
            <h2 id="shipping-delay-notice-title">{settings.title}</h2>
          </div>
        </div>
        <p id="shipping-delay-notice-description" className="shipping-delay-notice-update">
          {settings.updated_label}
        </p>
        <div className="shipping-delay-notice-list">
          {noticeItems(settings).map(({ icon: Icon, title, text }) => (
            <div className="shipping-delay-notice-item" key={title}>
              <Icon size={17} aria-hidden="true" />
              <p><strong>{title}.</strong> {text}</p>
            </div>
          ))}
        </div>
        <p className="shipping-delay-notice-footer">
          {settings.footer}
        </p>
        <button
          type="button"
          onClick={dismiss}
          className="shipping-delay-notice-action"
          data-testid="shipping-delay-notice-acknowledge-button"
        >
          Ho capito
        </button>
      </section>
    </div>
  );
};

export default ShippingDelayNotice;